import { useState, useEffect, useCallback } from "react";
import { useSessionContext } from "../../context/session.context";
import { useVeryNotificationsContext } from "../../components/common/v2/VeryNotifications/useVeryNotifications";
import {
  getListCampaigns,
  createCampaign,
  updateCampaign,
} from "../../services/coupon";
import styled from "styled-components";

/* ─── Styles ─── */
const Page = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 24px;
  overflow: auto;
  gap: 16px;
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  h2 {
    font-family: "Roboto", sans-serif;
    font-weight: 700;
    font-size: 20px;
    margin: 0;
  }
`;
const BtnAdd = styled.button`
  background: #1c1b1f;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 18px;
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: "Roboto", sans-serif;
  font-size: 13px;
  th {
    background: #f0f0f0;
    text-align: left;
    padding: 8px 12px;
    font-weight: 700;
  }
  td {
    padding: 8px 12px;
    border-bottom: 1px solid #e8e8e8;
    vertical-align: middle;
  }
  tr:hover td {
    background: #fafafa;
  }
`;
const Badge = styled.span`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 20px;
  font-weight: 700;
  background: ${(p) => (p.$active ? "#d4f5df" : "#f0f0f0")};
  color: ${(p) => (p.$active ? "#15803d" : "#666")};
`;
const ToggleBtn = styled.button`
  background: none;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
  &:hover {
    background: #f5f5f5;
  }
`;
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: #0005;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;
const Modal = styled.div`
  background: #fff;
  border-radius: 10px;
  padding: 28px 32px;
  width: 420px;
  max-width: 95vw;
  display: flex;
  flex-direction: column;
  gap: 16px;
  h3 {
    font-family: "Roboto", sans-serif;
    font-weight: 700;
    font-size: 17px;
    margin: 0;
  }
`;
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  label {
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    font-weight: 700;
    color: #555;
  }
  input {
    border: 1px solid #ccc;
    border-radius: 6px;
    padding: 7px 10px;
    font-size: 14px;
    font-family: "Roboto", sans-serif;
    outline: none;
    &:focus {
      border-color: #1c1b1f;
    }
  }
  span.hint {
    font-size: 11px;
    color: #888;
  }
`;
const ModalBtns = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 4px;
`;
const BtnCancel = styled.button`
  background: none;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 7px 16px;
  font-size: 14px;
  cursor: pointer;
`;

/* ─── Page Component ─── */
const getEmptyForm = () => ({
  name: "",
  startDate: "",
  endDate: "",
  amount: "",
  expirationDays: "90",
  maxClaims: "0",
});

export default function CampaignsPage() {
  const session = useSessionContext();
  const notis = useVeryNotificationsContext();
  const jwt = session.value?.jwt ?? "";

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(getEmptyForm());
  const [saving, setSaving] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    const resp = await getListCampaigns(jwt);
    setLoading(false);
    if (resp.error) {
      notis.createNotiError(resp.error.message, resp.error.details);
      return;
    }
    setList(resp.body?.list ?? []);
  }, [jwt]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const onChangeForm = (key, val) => {
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const onSubmitCreate = async () => {
    if (!form.name.trim()) {
      notis.createNotiError("El nombre es requerido");
      return;
    }
    if (!form.startDate || !form.endDate) {
      notis.createNotiError("Las fechas de inicio y fin son requeridas");
      return;
    }
    const amount = parseFloat(form.amount);
    if (!(amount > 0)) {
      notis.createNotiError("El monto debe ser mayor a 0");
      return;
    }
    setSaving(true);
    const resp = await createCampaign(
      form.name.trim(),
      form.startDate,
      form.endDate,
      amount,
      parseInt(form.expirationDays) || 90,
      parseInt(form.maxClaims) || 0,
      jwt,
    );
    setSaving(false);
    if (resp.error) {
      notis.createNotiError(resp.error.message, resp.error.details);
      return;
    }
    notis.createNotiSuccess("Campaña creada correctamente");
    setShowModal(false);
    setForm(getEmptyForm());
    loadList();
  };

  const onToggleActive = async (campaign) => {
    const newActive = !campaign.isActive;
    const resp = await updateCampaign(
      { campaignId: campaign.id, isActive: newActive },
      jwt,
    );
    if (resp.error) {
      notis.createNotiError(resp.error.message, resp.error.details);
      return;
    }
    setList((prev) =>
      prev.map((c) =>
        c.id === campaign.id ? { ...c, isActive: newActive } : c,
      ),
    );
    notis.createNotiSuccess(
      newActive ? "Campaña activada" : "Campaña desactivada",
      `La campaña "${campaign.name}" fue ${newActive ? "activada" : "desactivada"} correctamente`,
    );
  };

  return (
    <Page>
      <Header>
        <h2>Campañas de cupones</h2>
        <BtnAdd onClick={() => setShowModal(true)}>+ Nueva campaña</BtnAdd>
      </Header>

      {loading ? (
        <p style={{ fontFamily: "Roboto", fontSize: 14, color: "#888" }}>
          Cargando campañas...
        </p>
      ) : list.length === 0 ? (
        <p style={{ fontFamily: "Roboto", fontSize: 14, color: "#888" }}>
          No hay campañas registradas aún.
        </p>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Nombre</th>
              <th>Inicio</th>
              <th>Fin</th>
              <th>Monto</th>
              <th>Vigencia cupón</th>
              <th>Límite / Claims</th>
              <th>Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id}>
                <td>{c.id}</td>
                <td>{c.name}</td>
                <td>{c.startDate?.slice(0, 10) ?? "—"}</td>
                <td>{c.endDate?.slice(0, 10) ?? "—"}</td>
                <td>${c.amount} MXN</td>
                <td>{c.expirationDays} días</td>
                <td>
                  {c.maxClaims === 0 ? "Sin límite" : c.maxClaims} /{" "}
                  {c.claimsCount}
                </td>
                <td>
                  <Badge $active={!!c.isActive}>
                    {c.isActive ? "Activa" : "Inactiva"}
                  </Badge>
                </td>
                <td>
                  <ToggleBtn onClick={() => onToggleActive(c)}>
                    {c.isActive ? "Desactivar" : "Activar"}
                  </ToggleBtn>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {showModal && (
        <Overlay onClick={() => setShowModal(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <h3>Nueva campaña de cupones</h3>
            <Field>
              <label>Nombre</label>
              <input
                placeholder="Ej. Bienvenida primavera 2026"
                value={form.name}
                onChange={(e) => onChangeForm("name", e.target.value)}
              />
            </Field>
            <Field>
              <label>Fecha inicio</label>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => onChangeForm("startDate", e.target.value)}
              />
            </Field>
            <Field>
              <label>Fecha fin</label>
              <input
                type="date"
                value={form.endDate}
                onChange={(e) => onChangeForm("endDate", e.target.value)}
              />
            </Field>
            <Field>
              <label>Monto del cupón (MXN)</label>
              <input
                type="number"
                min="1"
                placeholder="Ej. 50"
                value={form.amount}
                onChange={(e) => onChangeForm("amount", e.target.value)}
              />
            </Field>
            <Field>
              <label>Vigencia del cupón (días desde el registro)</label>
              <input
                type="number"
                min="1"
                value={form.expirationDays}
                onChange={(e) => onChangeForm("expirationDays", e.target.value)}
              />
              <span className="hint">Por defecto 90 días</span>
            </Field>
            <Field>
              <label>Límite de usuarios (0 = sin límite)</label>
              <input
                type="number"
                min="0"
                value={form.maxClaims}
                onChange={(e) => onChangeForm("maxClaims", e.target.value)}
              />
            </Field>
            <ModalBtns>
              <BtnCancel onClick={() => setShowModal(false)}>
                Cancelar
              </BtnCancel>
              <BtnAdd disabled={saving} onClick={onSubmitCreate}>
                {saving ? "Guardando..." : "Crear campaña"}
              </BtnAdd>
            </ModalBtns>
          </Modal>
        </Overlay>
      )}
    </Page>
  );
}
